import React, { useState } from "react";
import { TspModal } from "../../../components/primitives";
import { buildVisitUpdatePayload, visitToDraft } from "../cityVisitsModel";

const Required = () => (
  <span className="tsp-required" aria-hidden="true">
    {" "}
    *
  </span>
);

// Edit a recorded visit. Journey and city are fixed (the server never moves a
// visit); the rest follows the same rules as Add City Visit, including the
// journey's date range. Nothing changes until the server has saved it.
export default function CityVisitEditModal({ visit, range, onClose, onSave }) {
  const [draft, setDraft] = useState(() => visitToDraft(visit));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const change = (key) => (event) => {
    setDraft((previous) => ({ ...previous, [key]: event.target.value }));
    setError("");
  };

  const save = async () => {
    const built = buildVisitUpdatePayload(draft, range);
    if (built.error) {
      setError(built.error);
      if (built.field) document.getElementById(`cv-edit-${built.field}`)?.focus();
      return;
    }
    setSaving(true);
    const result = await onSave(built.payload);
    setSaving(false);
    if (!result.ok) setError(result.message);
  };

  return (
    <TspModal
      open
      title="Edit City Visit"
      onClose={saving ? () => {} : onClose}
      footer={
        <>
          <button type="button" className="tsp-btn tsp-btn-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="tsp-btn tsp-btn-success" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </>
      }
    >
      <p className="tsp-muted-text tsp-cv-edit-place">
        {visit.journey.journey_name} · {visit.location.label || visit.location.city_name}
      </p>
      <div className="tsp-cv-edit-grid">
        <div className="tsp-form-field">
          <label htmlFor="cv-edit-date">
            Date
            <Required />
          </label>
          <input
            id="cv-edit-date"
            type="date"
            className="tsp-input"
            value={draft.date}
            min={range.startDate || undefined}
            max={range.endDate || undefined}
            disabled={saving}
            onChange={change("date")}
          />
        </div>
        <div className="tsp-form-field">
          <label htmlFor="cv-edit-time">
            Time
            <Required />
          </label>
          <input id="cv-edit-time" type="time" className="tsp-input" value={draft.time} disabled={saving} onChange={change("time")} />
        </div>
        <div className="tsp-form-field tsp-cv-edit-wide">
          <label htmlFor="cv-edit-areas">
            Areas Visited
          </label>
          <input id="cv-edit-areas" type="text" className="tsp-input" placeholder="Comma-separated, e.g. Fort, Market" value={draft.areasVisited} disabled={saving} onChange={change("areasVisited")} />
        </div>
        <div className="tsp-form-field">
          <label htmlFor="cv-edit-latitude">
            Latitude
            <Required />
          </label>
          <input id="cv-edit-latitude" type="text" inputMode="decimal" className="tsp-input" value={draft.latitude} disabled={saving} onChange={change("latitude")} />
        </div>
        <div className="tsp-form-field">
          <label htmlFor="cv-edit-longitude">
            Longitude
            <Required />
          </label>
          <input id="cv-edit-longitude" type="text" inputMode="decimal" className="tsp-input" value={draft.longitude} disabled={saving} onChange={change("longitude")} />
        </div>
        <div className="tsp-form-field tsp-cv-edit-wide">
          <label htmlFor="cv-edit-remarks">Remarks</label>
          <input id="cv-edit-remarks" type="text" className="tsp-input" value={draft.remarks} disabled={saving} onChange={change("remarks")} />
        </div>
      </div>
      {error && (
        <p className="tsp-form-error" role="alert">
          {error}
        </p>
      )}
    </TspModal>
  );
}
